import { setupSdk } from "@matterport/sdk";
import { useNavigate } from "react-router-dom";
import BedIcon from "../../assets/bed-icon.svg";
import PlayIcon from "../../assets/play-icon.svg";
import BathIcon from "../../assets/bath-icon.svg";
import SqftIcon from "../../assets/sqft-icon.svg";
import PauseIcon from "../../assets/pause-icon.svg";
import { reactions } from "../../services/reactions.ts";
import { formatPrice } from "../../utils/formatPrice.ts";
import { useUserProfileStore } from "../../stores/profileStore.ts";
import { generateAndSetZlid } from "../../utils/generateAndSetZlid.ts";
import { convertToFormattedAmount } from "../../utils/convertAmount.ts";
import type { RealsProperty } from "../../interfaces/realsProperties.ts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePropertyModalStore } from "../../stores/sharePropertyModalStore.ts";

const SDK_KEY = "qshq603zxt9nka78th0inux6a";
const SDK_IFRAME_URL = (sid: string) =>
  `https://my.matterport.com/show/?m=${sid}&play=1&start=1&qs=1&tourcta=0&help=0&mls=1&wh=0&title=0&brand=0&dh=1&hr=0&vr=0&kb=0&measure=0&layer=0&ts=-1&wts=2&tour=0&gt=0&mt=0&search=0&lp=1&st=1000`;

interface PropertiesVideoCardProps {
  property: RealsProperty;
}

const PropertiesVideoCard = ({ property }: PropertiesVideoCardProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();
  const [sdk, setSdk] = useState<any>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [shouldShowIframe, setShouldShowIframe] = useState(false);
  const [interaction, setInteraction] = useState(false);
  const [liked, setLiked] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const iframeSrc = useMemo(
    () => SDK_IFRAME_URL(property.matterport_id),
    [property.matterport_id]
  );
  const { userProfile } = useUserProfileStore();

  const { showPropertyModal, setShowPropertyModal } = usePropertyModalStore();
  useEffect(() => {
    const listingId = generateAndSetZlid(property.vendor, property.listing_id);

    const isLiked = userProfile?.likedHomes?.some(
      (home) => home.listingId === listingId
    );
    setLiked(!!isLiked);
  }, [userProfile, property]);

  const handleLike = async (): Promise<void> => {
    const listingId = generateAndSetZlid(property.vendor, property.listing_id);
    if (liked && listingId) {
      setLiked(false);
      await reactions(listingId, "REMOVE_SAVED_LISTING");
    } else if (!liked && listingId) {
      setLiked(true);
      await reactions(listingId, "SAVE_LISTING");
    }
  };

  const handlePlayPauseClick = useCallback(async () => {
    if (!sdk) {
      return;
    }
    try {
      setIsPlaying((prev) => !prev);
      setInteraction((prev) => !prev);
      if (isPlaying) {
        await sdk.Tour.stop();
      } else {
        await sdk.Tour.start();
      }
    } catch (err) {
      console.error("⚠️ Error toggling tour", err);
    }
  }, [sdk, isPlaying]);

  useEffect(() => {
    let debounceTimeout: ReturnType<typeof setTimeout> | null = null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        if (debounceTimeout) {
          clearTimeout(debounceTimeout);
        }

        debounceTimeout = setTimeout(() => {
          setShouldShowIframe(isVisible);

          if (!isVisible && sdk) {
            sdk.Tour.stop().catch(setSdk(null));
            setSdk(null);
            setInteraction(false);
            setIsPlaying(false);
          }
        }, 250);
      },
      { threshold: 0.6 }
    );

    const el = containerRef.current;
    if (el) {
      observer.observe(el);
    }

    return () => {
      if (el) {
        observer.unobserve(el);
        if (debounceTimeout) {
          clearTimeout(debounceTimeout);
        }
      }
    };
  }, [sdk]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let touchStartX = 0;
    let touchStartY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const deltaX = e.changedTouches[0].clientX - touchStartX;
      const deltaY = e.changedTouches[0].clientY - touchStartY;

      if (
        Math.abs(deltaX) > 100 &&
        Math.abs(deltaY) < 100 &&
        deltaX < 0 &&
        isPlaying
      ) {
        navigate(
          `/listing/${generateAndSetZlid(property.vendor, property.listing_id)}`
        );
      }
    };

    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isPlaying, navigate, property.listing_id, property.vendor]);

  const onIframeLoad = useCallback(async () => {
    if (!iframeRef.current) {
      return;
    }

    try {
      const sdkInstance = await setupSdk(SDK_KEY, {
        iframe: iframeRef.current,
      });
      setSdk(sdkInstance);

      setTimeout(() => {
        // const mode = sdkInstance.Mode.Mode.OUTSIDE;
        // const transition = sdkInstance.Mode.TransitionType.FLY;

        // sdkInstance.Mode.moveTo(mode, { transition }).catch(console.error);

        // sdkInstance.Tour.transition.subscribe(function (transition) {
        //   // the transition has changed
        //   console.log("Current transition is ", transition.from, transition.to);
        // });

        sdkInstance.Tour.start(2).catch(console.error);
        setIsPlaying(true);
        setInteraction(false);
      }, 500);
    } catch (err) {
      console.error("❌ SDK setup failed on iframe load", err);
    }
  }, []);
  useEffect(() => {
    const container = containerRef.current;
    if (!isPlaying || !sdk || !container) return;

    const handleClickWithinComponent = async (event: MouseEvent) => {
      const detailsSection = container.querySelector(".details-section");
      const pauseButtonWrapper = container.querySelector(
        ".pause-button-wrapper"
      );

      const clickedInsideDetails = detailsSection?.contains(
        event.target as Node
      );
      const clickedInsidePause = pauseButtonWrapper?.contains(
        event.target as Node
      );

      if (!clickedInsideDetails && !clickedInsidePause) {
        try {
          setIsPlaying(false);
          setInteraction(true);
          await sdk.Tour.stop();
        } catch (err) {
          console.error("Failed to stop tour on inside component click:", err);
        }
      }
    };

    container.addEventListener("click", handleClickWithinComponent);
    return () => {
      container.removeEventListener("click", handleClickWithinComponent);
    };
  }, [isPlaying, sdk]);

  return (
    <div ref={containerRef} className="h-full w-full relative">
      <div className="w-full h-full relative">
        {shouldShowIframe ? (
          <iframe
            ref={iframeRef}
            className="w-full h-full object-cover"
            allow="xr-spatial-tracking"
            allowFullScreen
            style={{ pointerEvents: interaction ? "auto" : "none" }}
            title="Matterport Tour"
            src={iframeSrc}
            onLoad={onIframeLoad}
          />
        ) : (
          <img
            src={property.display_image_url}
            alt="Preview"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}
        <div className="absolute top-0 z-20 flex justify-center pt-4">
          <div className="px-4 w-full flex justify-start items-center pause-button-wrapper">
            <button
              type="button"
              className="h-12 w-12 rounded-full shadow-lg bg-white/10 flex items-center justify-center cursor-pointer"
              onClick={() => handlePlayPauseClick()}
            >
              <img src={isPlaying ? PauseIcon : PlayIcon} alt="Play/Pause" />
            </button>
          </div>
        </div>
        {!interaction && (
          <div className="absolute bottom-0 w-full z-20 flex justify-center pb-4 details-section">
            <div className="px-4 flex items-end w-full gap-2">
              <div className="w-full space-y-3">
                <div className="flex items-center justify-between w-full">
                  <div>
                    <div className="text-2xl font-bold text-white">
                      $
                      {formatPrice(
                        convertToFormattedAmount(property.list_price)
                      )}
                    </div>
                    {property.address && (
                      <div className="text-sm font-medium text-white">
                        {property.address.split(",")[0]}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() =>
                      navigate(
                        `/listing/${generateAndSetZlid(property.vendor, property.listing_id)}`
                      )
                    }
                    type="button"
                    className="text-xs text-white font-medium px-4 py-1.5 bg-black whitespace-nowrap rounded-full cursor-pointer"
                  >
                    View Listing
                  </button>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-sm text-white">
                      <img src={BedIcon} alt="" />
                      {property.bedrooms_total || "N/A"}
                    </div>
                    <div className="text-white">•</div>
                    <div className="flex items-center gap-1 text-sm text-white">
                      <img src={BathIcon} alt="" />
                      {property.bathrooms_total_integer || "N/A"}
                    </div>
                    <div className="text-white">•</div>
                    <div className="flex items-center gap-1 text-sm text-white">
                      <img src={SqftIcon} alt="" />
                      {property.living_area
                        ? `${property.living_area} Sq. ft`
                        : property.living_area_lower &&
                            property.living_area_upper
                          ? `${property.living_area_lower} - ${property.living_area_upper} Sq. ft`
                          : "N/A"}
                    </div>
                  </div>
                  {property.description && (
                    <div
                      className="text-white text-sm"
                      onClick={() =>
                        setShowFullDescription(!showFullDescription)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setShowFullDescription(!showFullDescription);
                        }
                      }}
                    >
                      <p
                        className={`${showFullDescription ? "line-clamp-5" : "line-clamp-2"} transition-all`}
                      >
                        {property.description}
                      </p>
                    </div>
                  )}
                </div>

                {(property.locality || property.administrative_area) && (
                  <div className="py-1 px-3 rounded-full bg-z-dark-moss-green text-surface font-medium w-fit border border-white/30 whitespace-nowrap">
                    {`${property.locality ?? ""}${property.administrative_area ? `${property.locality ? ", " : ""}${property.administrative_area}` : ""}`}
                  </div>
                )}
              </div>

              <div className="flex space-y-4 items-center flex-col">
                <button
                  className="flex flex-col items-center justify-center text-sm font-medium w-full text-white space-y-4 px-4 py-2 cursor-pointer"
                  onClick={() => handleLike()}
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <mask
                      id="mask0_5528_2585"
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="24"
                      height="24"
                    >
                      <rect width="24" height="24" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_5528_2585)">
                      <path
                        d="M12 19.6537L11.2423 18.9692C9.61025 17.4782 8.26025 16.2019 7.19225 15.1404C6.12442 14.0787 5.2815 13.1425 4.6635 12.3317C4.0455 11.5208 3.61375 10.7872 3.36825 10.1307C3.12275 9.47432 3 8.81407 3 8.1499C3 6.87807 3.43208 5.81015 4.29625 4.94615C5.16025 4.08199 6.22817 3.6499 7.5 3.6499C8.3795 3.6499 9.2045 3.8749 9.975 4.3249C10.7455 4.7749 11.4205 5.4294 12 6.2884C12.5795 5.4294 13.2545 4.7749 14.025 4.3249C14.7955 3.8749 15.6205 3.6499 16.5 3.6499C17.7718 3.6499 18.8398 4.08199 19.7038 4.94615C20.5679 5.81015 21 6.87807 21 8.1499C21 8.81407 20.8773 9.47432 20.6318 10.1307C20.3862 10.7872 19.9545 11.5208 19.3365 12.3317C18.7185 13.1425 17.8787 14.0787 16.8173 15.1404C15.7558 16.2019 14.4026 17.4782 12.7578 18.9692L12 19.6537Z"
                        className={`${liked ? "fill-red-400" : "fill-white"}`}
                      />
                    </g>
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setShowPropertyModal(
                      !showPropertyModal,
                      property.listing_id
                    )
                  }
                  className="flex flex-col items-center justify-center text-sm font-medium w-full text-white space-y-4 px-3 cursor-pointer py-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M20.7914 12.6075C21.0355 12.3982 21.1575 12.2936 21.2023 12.1691C21.2415 12.0598 21.2415 11.9403 21.2023 11.831C21.1575 11.7065 21.0355 11.6019 20.7914 11.3926L12.3206 4.13202C11.9004 3.77182 11.6903 3.59172 11.5124 3.58731C11.3578 3.58348 11.2101 3.6514 11.1124 3.77128C11 3.90921 11 4.18595 11 4.73942V9.03468C8.86532 9.40813 6.91159 10.4898 5.45971 12.1139C3.87682 13.8846 3.00123 16.176 3 18.551V19.163C4.04934 17.8989 5.35951 16.8766 6.84076 16.166C8.1467 15.5395 9.55842 15.1684 11 15.0706V19.2607C11 19.8141 11 20.0909 11.1124 20.2288C11.2101 20.3487 11.3578 20.4166 11.5124 20.4128C11.6903 20.4084 11.9004 20.2283 12.3206 19.8681L20.7914 12.6075Z"
                      fill="white"
                      stroke="white"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/listing/${generateAndSetZlid(property.vendor, property.listing_id)}`
                    )
                  }
                  className="text-white text-sm font-medium flex flex-col items-center gap-2 cursor-pointer"
                >
                  {property.display_image_url && (
                    <img
                      src={property.display_image_url}
                      alt=""
                      className="object-cover w-10 h-10 rounded-full"
                    />
                  )}
                  View
                </button>
              </div>
            </div>
          </div>
        )}
        {!interaction && (
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black z-10 pointer-events-none" />
        )}
      </div>
    </div>
  );
};
export { PropertiesVideoCard };
